import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import AppPageHeader from './AppPageHeader';
import PageTabs from './PageTabs';
import ActionToolbar from './ActionToolbar';
import { MODULES } from './navigation';
import useMenuAccess from './useMenuAccess';
import { color } from './tokens';

/**
 * THE module shell - used by every module (Front Desk, Restaurant, HR...).
 *
 *   Compact Header + Quick Actions
 *   Horizontal Tabs
 *   <Outlet/>  <- the existing page renders here, routes UNCHANGED
 *
 * Tabs are `views` (navigate). Actions are `tasks` (New Booking...).
 * Both are filtered by the same permission + plan rule as the sidebar.
 */
export default function ModuleLayout({ moduleKey }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { hasKey, planAllowed } = useMenuAccess();

  const mod = MODULES.find((m) => m.key === moduleKey);
  if (!mod) return <Outlet />;

  const ok = (k) => hasKey(k) && planAllowed(k);
  const tabs = (mod.tabs || []).filter((t) => ok(t.menuKey));
  const actions = (mod.actions || []).filter((a) => ok(a.menuKey));

  const path = (loc.pathname || '').toLowerCase();
  const active =
    tabs.find((t) => path === t.path.toLowerCase())?.key ||
    tabs.find((t) => path.startsWith(t.path.toLowerCase()))?.key ||
    tabs[0]?.key;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: color.bg, minHeight: '100%' }}>
      <AppPageHeader
        title={mod.label}
        subtitle={mod.subtitle}
        actions={
          <ActionToolbar
            actions={actions.map((a) => ({
              label: a.label,
              variant: a.primary ? 'primary' : 'default',
              onClick: () => nav(a.path),
            }))}
          />
        }
      />
      <PageTabs
        tabs={tabs.map((t) => ({ key: t.key, label: t.label }))}
        value={active}
        onChange={(k) => {
          const t = tabs.find((x) => x.key === k);
          if (t) nav(t.path);
        }}
      />
      <Outlet />
    </Box>
  );
}